import React, { useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import serverLogo from "assets/logos/server.png";
import { APP_LINKS } from "config/appConfig";
import { createAccountSummary } from "domain/account";
import { formatBalance } from "utils/formatters";
import useUserProfile from "features/account/hooks/useUserProfile";
import { useAuth } from "features/account/context/AuthContext";
import { PlusIcon } from "shared/icons";
import useDismissibleLayer from "shared/hooks/useDismissibleLayer";
import { useTranslation } from "shared/hooks/useTranslation";
import LightButton from "shared/ui/LightButton";
import "./UserPanel.css";

const DonateButton = ({ balance, donateUrl }) => {
  const formatted = formatBalance(balance);

  const handleClick = () => {
    window.open(donateUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <LightButton
      onClick={handleClick}
      className="sr-user__donate-btn"
      ariaLabel="Donate via Donatello"
      icon={<PlusIcon size={20} color="#000" />}
    >
      <span className="sr-user__balance">{formatted}</span>
    </LightButton>
  );
};

DonateButton.propTypes = {
  balance: PropTypes.number,
  donateUrl: PropTypes.string.isRequired,
};

const UserPanel = ({ onLogout }) => {
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const profileRef = useRef(null);
  const avatarButtonRef = useRef(null);
  const menuId = useId();
  const buttonId = useId();

  const toggleOpen = () => setIsOpen((value) => !value);
  const closeMenu = () => setIsOpen(false);
  const handleLogout = () => {
    closeMenu();
    if (typeof onLogout === "function") onLogout();
  };

  useDismissibleLayer({
    isOpen,
    onClose: closeMenu,
    containerRef: profileRef,
    returnFocusRef: avatarButtonRef,
  });

  if (!user) return null;

  const account = createAccountSummary({
    user,
    profile,
    fallbackAvatar: serverLogo,
  });
  const username = account.username;
  const avatar = account.avatar;
  const donateUrl = account.donateUrl;

  return (
    <div className="sr-user">
      <div className="sr-user__panel">
        <DonateButton balance={account.balance} donateUrl={donateUrl} />
        <div
          className={`sr-user__profile${isOpen ? " is-open" : ""}`}
          ref={profileRef}
        >
          <button
            ref={avatarButtonRef}
            type="button"
            className="sr-user__avatar-btn"
            onClick={toggleOpen}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-controls={menuId}
            aria-label={t("userPanel.openUserMenu")}
            id={buttonId}
          >
            <img
              className="sr-user__avatar"
              src={avatar}
              alt=""
              width="40"
              height="40"
            />
          </button>

          <div
            className="sr-user__menu"
            role="menu"
            id={menuId}
            aria-labelledby={buttonId}
          >
            <div
              className="sr-user__menu-header"
              aria-label={t("userPanel.userInfo")}
            >
              <img
                className="sr-user__menu-avatar"
                src={avatar}
                alt=""
                width="40"
                height="40"
              />
              <div className="sr-user__menu-username">{username}</div>
            </div>

            <div
              className="sr-user__menu-divider"
              role="separator"
              aria-hidden="true"
            ></div>
            <a
              className="sr-user__menu-item"
              href={APP_LINKS.voteServer}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
            >
              <span
                className="sr-user__menu-icon is-vote"
                aria-hidden="true"
              ></span>
              <span className="sr-user__menu-label">
                {t("userPanel.voteForServer")}
              </span>
            </a>
            <a
              className="sr-user__menu-item"
              href={donateUrl}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
            >
              <span
                className="sr-user__menu-icon is-donut"
                aria-hidden="true"
              ></span>
              <span className="sr-user__menu-label">
                {t("userPanel.donate")}
              </span>
            </a>
            <div
              className="sr-user__menu-divider"
              role="separator"
              aria-hidden="true"
            ></div>
            <button
              className="sr-user__menu-item is-danger"
              onClick={handleLogout}
              role="menuitem"
            >
              <span
                className="sr-user__menu-icon is-exit"
                aria-hidden="true"
              ></span>
              <span className="sr-user__menu-label">{t("userPanel.exit")}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

UserPanel.propTypes = {
  onLogout: PropTypes.func,
};

export default UserPanel;
